import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { DialogContent, DialogTitle } from '@material-ui/core';
import { isFunction } from 'lodash';
import Dialog from '../../../dialogs/components/Dialog';
import { IdPropType } from '../../../../proptypes/basic';
import { useCallbackRef } from '../../../hooks';
import DialogFormActions from '../../../dialogs/components/DialogFormActions';
import SendMailForm from './SendMailForm';
import {
    ExtraEntitiesPropType,
    MailAttachmentPropType,
    PrefillMailPropType,
} from '../../proptypes';
import LoadingBox from '../../../loading/components/LoadingBox';
import { useCustomer } from '../../../customers/customersSlice';
import {
    selectAllEmailTemplates,
    useEmailTemplates,
} from '../../../mailTemplates/emailTemplateSlice';

import {
    selectAllEmailDrafts,
    useEmailDraft,
    useEmailDrafts,
} from '../../../mails/emailDraftsSlice';

const SendMailDialog = ({
    onClose,
    entity,
    entityId,
    templateId,
    attachments,
    extraEntities: prepareExtraEntities,
    prefill,
    onSent,
    draftId,
    onCloseWithoutSent,
}) => {
    const ref = useCallbackRef();
    const draftSaveRef = useRef(() => Promise.resolve());

    // Wait for onSent (creates the NurseRequest/etc. bookkeeping the
    // dashboard reads) before closing - the email itself is already sent by
    // this point, but closing unconditionally would hide a failure here
    // behind a dialog that just quietly disappears, leaving the dashboard
    // stale with no indication anything went wrong. Not catching the
    // rejection here is deliberate: SendMailForm's own bookkeeping .catch
    // already reports it to the user - it just needs the dialog to stay
    // open when it fires.
    const handleSent = useCallback(
        (result) => Promise.resolve(onSent ? onSent(result) : null).then(() => onClose(result)),
        [onClose, onSent]
    );

    const handleCloseWithoutSent = useCallback(() => {
        // Wait until recipient and other field autosaves have reached the
        // backend before the parent reloads the draft list.
        Promise.resolve(draftSaveRef.current())
            .catch((error) => {
                console.error('SendMailDialog: error saving draft before close =>', error);
            })
            .then(() => {
                if (onCloseWithoutSent) {
                    onCloseWithoutSent();
                }
                onClose();
            });
    }, [onCloseWithoutSent, onClose]);

    useEmailDrafts();
    const allEmailDraftsForEmployee = useSelector(selectAllEmailDrafts);
    //console.log('allEmailDraftsForEmployee =>', allEmailDraftsForEmployee);

    const storedDraft = draftId && allEmailDraftsForEmployee.find((d) => d.id === draftId);
    //console.log('storedDraft =>',storedDraft, ' draftId =>', draftId);

    const [draft, { loading: draftLoading }] = useEmailDraft(draftId);
    //console.log('draft for draftId =>', draft);

    useEmailTemplates();
    const emailTemplates = useSelector(selectAllEmailTemplates);
    const realTemplateId = useMemo(() => {
        if (templateId) {
            return templateId;
        }
        // maybe the draft is based on a template
        if (storedDraft && storedDraft.emailTemplateId) {
            return storedDraft.emailTemplateId;
        }

        if (emailTemplates) {
            const defaultTemplate = emailTemplates.find(({ isDefault }) => !!isDefault);
            return defaultTemplate?.id || null;
        }

        return null;
    }, [templateId, emailTemplates, storedDraft]);

    // TODO: move to form -> prevent tag replacing until the calls are resolved
    let [, { loading, initialized }] = useCustomer(entityId);
    if (entityId == null) {
        loading = false;
        initialized = true;
    }
    const [extraEntities, setExtraEntities] = useState(undefined);
    useEffect(() => {
        Promise.resolve(
            isFunction(prepareExtraEntities) ? prepareExtraEntities() : prepareExtraEntities
        ).then(setExtraEntities);
    }, [prepareExtraEntities, setExtraEntities]);

    return (
        <Dialog fullWidth maxWidth="lg">
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                E-Mail versenden
            </DialogTitle>

            <DialogContent>
                <LoadingBox
                    loading={extraEntities === undefined || loading || draftLoading}
                    initialized={initialized}
                >
                    {extraEntities !== undefined && !loading && initialized && (
                        <SendMailForm
                            entity={entity}
                            entityId={entityId}
                            templateId={realTemplateId}
                            extraEntities={extraEntities}
                            attachments={attachments}
                            onSent={handleSent}
                            submitRef={ref}
                            prefill={prefill}
                            draft={draft ?? null}
                            draftLoading={draftLoading}
                            draftId={draftId}
                            draftSaveRef={draftSaveRef}
                        />
                    )}
                </LoadingBox>
            </DialogContent>

            <DialogFormActions onClose={handleCloseWithoutSent} ref={ref} />
        </Dialog>
    );
};

SendMailDialog.propTypes = {
    entity: PropTypes.string.isRequired,
    entityId: IdPropType,
    templateId: IdPropType,
    attachments: PropTypes.arrayOf(MailAttachmentPropType),
    prefill: PrefillMailPropType,
    draftId: IdPropType,
    extraEntities: ExtraEntitiesPropType,
    onClose: PropTypes.func.isRequired,
    onSent: PropTypes.func,
    onDeleted: PropTypes.func,
    draftLoading: PropTypes.bool,
};

SendMailDialog.defaultProps = {
    templateId: null,
    attachments: null,
    prefill: null,
    draftId: null,
    extraEntities: null,
    onSent: null,
    entityId: null,
    onDeleted: null,
    draftLoading: false,
};

export default SendMailDialog;
